import { useState, useRef, useCallback } from 'react'
import { getCopilotToken } from '../scripts/copilot.auth'
import { CopilotMessage, CopilotActionProposed, CopilotWSEvent } from '../models/copilot.model'

const WS_URL = 'wss://copilot.digitaladmin.io'

let __msgSeq = 0
function nextMsgId(prefix: string): string {
  __msgSeq += 1
  return `${prefix}-${Date.now()}-${__msgSeq}`
}

export type WSStatus = 'idle' | 'connecting' | 'open' | 'closed'

export function useCopilotWebSocket() {
  const [status, setStatus] = useState<WSStatus>('idle')
  const [messages, setMessages] = useState<CopilotMessage[]>([])
  const [streaming, setStreaming] = useState(false)
  const [currentToolRunning, setCurrentToolRunning] = useState<string | undefined>()
  const [pendingAction, setPendingAction] = useState<CopilotActionProposed | undefined>()

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempted = useRef(false)
  const streamingRef = useRef(false)

  const appendAssistantDelta = useCallback((delta: string) => {
    setMessages(prev => {
      const last = prev[prev.length - 1]
      if (last && last.role === 'assistant' && last.id === '__streaming__') {
        return [...prev.slice(0, -1), { ...last, content: last.content + delta }]
      }
      const msg: CopilotMessage = { id: '__streaming__', role: 'assistant', content: delta, createdAt: new Date().toISOString() }
      return [...prev, msg]
    })
  }, [])

  const pushSystemMessage = useCallback((content: string, extra?: Partial<CopilotMessage>) => {
    const msg: CopilotMessage = { id: nextMsgId('sys'), role: 'system', content, createdAt: new Date().toISOString(), ...extra }
    setMessages(prev => [...prev, msg])
  }, [])

  const handleEvent = useCallback((event: CopilotWSEvent) => {
    switch (event.type) {
      case 'token':
        setCurrentToolRunning(undefined)
        appendAssistantDelta(event.delta)
        break

      case 'tool_running':
        setCurrentToolRunning(event.toolName)
        break

      case 'action_proposed':
        setCurrentToolRunning(undefined)
        setPendingAction({ actionId: event.actionId, toolName: event.toolName, preview: event.preview, description: event.description, status: 'pending' })
        break

      case 'action_result': {
        const toolName = event.toolName ?? pendingAction?.toolName ?? ''
        setPendingAction(undefined)
        setStreaming(true)
        streamingRef.current = true
        if (event.success) {
          const resultId = typeof event.result === 'object' && event.result ? (event.result.id ?? event.result._id ?? '') : ''
          pushSystemMessage('', { actionToolName: toolName, actionSuccess: true, actionResultId: resultId || undefined })
        } else {
          const errMsg = typeof event.error === 'string' ? event.error : ''
          pushSystemMessage('', { actionToolName: toolName, actionSuccess: false, actionError: errMsg })
        }
        break
      }

      case 'done':
        setStreaming(false)
        streamingRef.current = false
        setCurrentToolRunning(undefined)
        setMessages(prev => {
          const last = prev[prev.length - 1]
          if (last && last.id === '__streaming__') {
            return [...prev.slice(0, -1), { ...last, id: event.messageId || nextMsgId('msg') }]
          }
          return prev
        })
        break

      case 'batch_update': {
        const progress = event.progress != null && event.total != null ? ` (${event.progress}/${event.total})` : ''
        pushSystemMessage(`${event.milestone}${progress}`, { actionToolName: 'batch_update' })
        break
      }

      case 'error':
        setStreaming(false)
        streamingRef.current = false
        setCurrentToolRunning(undefined)
        pushSystemMessage(event.message || 'An error occurred')
        break
    }
  }, [appendAssistantDelta, pushSystemMessage, pendingAction])

  const connectWS = useCallback(() => {
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return
    }

    const token = getCopilotToken()
    if (!token) {
      setStatus('closed')
      return
    }

    setStatus('connecting')
    const ws = new WebSocket(`${WS_URL}?token=${token}`)

    ws.onopen = () => {
      setStatus('open')
      reconnectAttempted.current = false
    }

    ws.onmessage = (e) => {
      try {
        const data: CopilotWSEvent = JSON.parse(e.data)
        handleEvent(data)
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      setStatus('closed')
      wsRef.current = null

      if (streamingRef.current && !reconnectAttempted.current) {
        reconnectAttempted.current = true
        setTimeout(() => connectWS(), 500)
      }
    }

    ws.onerror = () => {
      // onclose will fire after this
    }

    wsRef.current = ws
  }, [handleEvent])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
      wsRef.current = null
    }
    setStatus('idle')
    setStreaming(false)
    streamingRef.current = false
    setCurrentToolRunning(undefined)
    setPendingAction(undefined)
  }, [])

  const sendUserMessage = useCallback((threadId: string, content: string) => {
    const msg: CopilotMessage = { id: nextMsgId('user'), role: 'user', content, createdAt: new Date().toISOString() }
    setMessages(prev => [...prev, msg])
    setStreaming(true)
    streamingRef.current = true
    setCurrentToolRunning(undefined)
    setPendingAction(undefined)

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'user_message', threadId, content }))
    }
  }, [])

  const confirmAction = useCallback((actionId: string) => {
    setPendingAction(prev => prev ? { ...prev, status: 'confirming' } : undefined)
    setStreaming(true)
    streamingRef.current = true
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'confirm_action', actionId }))
    }
  }, [])

  const rejectAction = useCallback((actionId: string) => {
    setPendingAction(prev => prev ? { ...prev, status: 'rejecting' } : undefined)
    setStreaming(true)
    streamingRef.current = true
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'reject_action', actionId }))
    }
  }, [])

  const loadMessages = useCallback((msgs: CopilotMessage[]) => {
    setMessages(msgs)
    setStreaming(false)
    streamingRef.current = false
    setCurrentToolRunning(undefined)
    setPendingAction(undefined)
  }, [])

  const resetMessages = useCallback(() => {
    setMessages([])
    setStreaming(false)
    streamingRef.current = false
    setCurrentToolRunning(undefined)
    setPendingAction(undefined)
  }, [])

  return {
    status,
    messages,
    streaming,
    currentToolRunning,
    pendingAction,
    connectWS,
    disconnect,
    sendUserMessage,
    confirmAction,
    rejectAction,
    loadMessages,
    resetMessages,
    reconnect: connectWS,
  }
}
